import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, FileCheck2, Radar, Scale } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { SECTION_IDS, useSectionNav } from "@/lib/navigation";

const SERIES = [
  { m: "01", v: 100 },
  { m: "02", v: 103 },
  { m: "03", v: 99 },
  { m: "04", v: 107 },
  { m: "05", v: 112 },
  { m: "06", v: 109 },
  { m: "07", v: 118 },
  { m: "08", v: 124 },
  { m: "09", v: 121 },
  { m: "10", v: 131 },
  { m: "11", v: 138 },
  { m: "12", v: 144 },
];

export const Hero = () => {
  const { t } = useTranslation();
  const goToSection = useSectionNav();

  const chips = [
    { icon: FileCheck2, label: t("hero.chips.one") },
    { icon: Radar, label: t("hero.chips.two") },
    { icon: Scale, label: t("hero.chips.three") },
  ];

  return (
    <section
      id={SECTION_IDS.home}
      className="relative overflow-hidden border-b border-border bg-background"
      data-testid="home-hero"
    >
      <div className="strata-band absolute inset-x-0 top-0 h-20" aria-hidden="true" />
      <div
        className="blueprint-grid pointer-events-none absolute inset-0 opacity-[0.35]"
        aria-hidden="true"
      />
      <div className="container-page relative grid grid-cols-1 gap-12 py-16 sm:py-20 lg:grid-cols-12 lg:gap-10 lg:py-24">
        <div className="lg:col-span-7">
          <p className="eyebrow" data-testid="hero-eyebrow">
            {t("hero.eyebrow")}
          </p>
          <h1 className="mt-4 max-w-3xl text-[32px] font-semibold leading-[1.08] tracking-tight text-foreground sm:text-5xl lg:text-[56px]">
            {t("hero.title")}
          </h1>
          <p className="mt-6 max-w-2xl border-l-2 border-[hsl(var(--terracotta))] pl-4 text-base leading-relaxed text-[hsl(var(--ink-2))] sm:text-lg">
            {t("hero.lead")}
          </p>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            {t("hero.p1")}
          </p>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            {t("hero.p2")}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button
              type="button"
              onClick={() => goToSection(SECTION_IDS.contact)}
              className="h-12 rounded-lg px-6 text-sm"
              data-testid="hero-primary-cta"
            >
              {t("hero.cta_primary")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => goToSection(SECTION_IDS.circuit)}
              className="h-12 rounded-lg border-border bg-card px-6 text-sm"
              data-testid="hero-secondary-cta"
            >
              {t("hero.cta_secondary")}
            </Button>
          </div>

          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
            {chips.map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="flex items-center gap-2 text-xs text-[hsl(var(--ink-2))]"
              >
                <Icon className="h-4 w-4 text-[hsl(var(--terracotta))]" />
                {label}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5">
          <div
            className="rounded-xl border border-border bg-card p-5 shadow-[0_10px_30px_hsl(214_30%_10%/0.08)] sm:p-6"
            data-testid="hero-data-core"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-base font-semibold text-foreground">
                  {t("hero.core_title")}
                </h2>
                <p className="font-data mt-1 text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--ink-3))]">
                  {t("hero.core_subtitle")}
                </p>
              </div>
              <span className="font-data rounded-md bg-secondary px-2 py-1 text-[10px] text-[hsl(var(--ink-2))]">
                {t("hero.core_updated")}
              </span>
            </div>

            <div className="mt-5 h-[168px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={SERIES} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="litiosurArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(214 62% 18%)" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="hsl(214 62% 18%)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <YAxis hide domain={[90, 155]} />
                  <Area
                    type="monotone"
                    dataKey="v"
                    stroke="hsl(214 62% 18%)"
                    strokeWidth={2}
                    fill="url(#litiosurArea)"
                    isAnimationActive={false}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <p className="font-data mt-1 text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--ink-3))]">
              {t("hero.core_series_label")}
            </p>
            <div className="mt-4 flex items-end justify-between border-t border-border pt-4">
              <span className="font-data text-3xl font-semibold text-foreground">144,0</span>
              <span className="font-data text-sm font-medium text-[hsl(var(--terracotta))]">
                +44,0%
              </span>
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
              {t("hero.core_note")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
